import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';

import { api, unwrapPage } from '@/api/client';
import type { components } from '@/api/schema';
import { PermissionGate } from '@/auth/PermissionGate';
import { Cargando, Encabezado, ErrorCarga, Vacio } from '@/components/Estados';
import { Paginador } from '@/components/Paginador';
import { fechaHora } from '@/lib/formato';

type Evento = components['schemas']['AuditEvent'];

/** Filtros en la URL: se pueden compartir y sobreviven a recargar la página. */
function useFiltros() {
  const [params, setParams] = useSearchParams();
  const filtros = {
    page: Number(params.get('page') ?? '1') || 1,
    action: params.get('action') ?? '',
    entity: params.get('entity') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
  };
  const cambiar = (cambios: Partial<typeof filtros>) => {
    const next = { ...filtros, page: 1, ...cambios };
    const p = new URLSearchParams();
    Object.entries(next).forEach(([k, v]) => {
      if (v !== '' && !(k === 'page' && v === 1)) p.set(k, String(v));
    });
    setParams(p, { replace: true });
  };
  return { filtros, cambiar };
}

function Tabla() {
  const { filtros, cambiar } = useFiltros();
  const q = useQuery({
    queryKey: ['auditoria', filtros],
    queryFn: () =>
      unwrapPage<Evento[]>(
        api.GET('/api/v1/admin/audit-events', {
          params: {
            query: {
              page: filtros.page,
              pageSize: 50,
              ...(filtros.action && { action: filtros.action }),
              ...(filtros.entity && { entity: filtros.entity }),
              ...(filtros.from && { from: filtros.from }),
              ...(filtros.to && { to: filtros.to }),
            },
          },
        }),
      ),
    placeholderData: keepPreviousData,
  });

  return (
    <>
      <form
        className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4"
        onSubmit={(e) => e.preventDefault()}
        aria-label="Filtros"
      >
        <input
          className="input-vidrio"
          placeholder="Acción (p. ej. auth.)"
          defaultValue={filtros.action}
          onBlur={(e) => cambiar({ action: e.target.value.trim() })}
          aria-label="Acción"
        />
        <input
          className="input-vidrio"
          placeholder="Entidad (p. ej. staff_user)"
          defaultValue={filtros.entity}
          onBlur={(e) => cambiar({ entity: e.target.value.trim() })}
          aria-label="Entidad"
        />
        <input
          type="date"
          className="input-vidrio"
          value={filtros.from}
          onChange={(e) => cambiar({ from: e.target.value })}
          aria-label="Desde"
        />
        <input
          type="date"
          className="input-vidrio"
          value={filtros.to}
          onChange={(e) => cambiar({ to: e.target.value })}
          aria-label="Hasta"
        />
      </form>
      {q.isPending && <Cargando />}
      {q.isError && <ErrorCarga error={q.error} onReintentar={() => void q.refetch()} />}
      {q.data && q.data.data.length === 0 && <Vacio titulo="Sin eventos con esos filtros" />}
      {q.data && q.data.data.length > 0 && (
        <div className="panel-vidrio overflow-x-auto p-0">
          <table className="tabla-cs w-full text-sm">
            <thead>
              <tr>
                <th>Fecha</th>
                <th>Staff</th>
                <th>Acción</th>
                <th>Entidad</th>
                <th>IP</th>
                <th>Detalle</th>
              </tr>
            </thead>
            <tbody>
              {q.data.data.map((e) => (
                <tr key={e.id}>
                  <td className="whitespace-nowrap">{fechaHora(e.occurredAt)}</td>
                  <td>{e.staffName ?? '—'}</td>
                  <td className="font-mono text-xs">{e.action}</td>
                  <td className="font-mono text-xs">
                    {e.entity ? `${e.entity}${e.entityId ? ` #${e.entityId}` : ''}` : '—'}
                  </td>
                  <td className="text-xs">{e.ip ?? '—'}</td>
                  <td className="max-w-md">
                    {(e.before !== null || e.after !== null) && (
                      <details>
                        <summary className="cursor-pointer text-xs font-semibold">Ver</summary>
                        <pre className="mt-2 max-h-60 overflow-auto rounded-lg bg-[var(--pista)] p-2 text-xs whitespace-pre-wrap">
                          {JSON.stringify({ antes: e.before, despues: e.after }, null, 2)}
                        </pre>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {q.data && (
        <Paginador
          page={q.data.page}
          pageSize={q.data.pageSize}
          total={q.data.total}
          onPage={(page) => cambiar({ page })}
        />
      )}
    </>
  );
}

export function AuditoriaPage() {
  return (
    <PermissionGate seccion="staff">
      <Encabezado
        titulo="Auditoría"
        descripcion="Todo lo que hace el staff en el panel queda registrado aquí."
      />
      <Tabla />
    </PermissionGate>
  );
}
